import { type GetAskPunttProjectResponse } from "@mg/schemas/src/christo/askPunttProject";
import { CircleNotch, Lock, Plus, Users } from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";

type ProjectChat = GetAskPunttProjectResponse["chats"][number];

/**
 * Left thread rail for a campaign/project workspace. Splits the project's
 * chats into the team-visible "Project" shared chat and the requester's
 * private "Personal" chats, mirroring Campaigns.html. Selecting a thread
 * drives the embedded chat renderer; "New" starts a private chat.
 */
export function CampaignThreadRail({
  chats,
  sharedChatId,
  currentUserId,
  activeChatId,
  onSelect,
  onNewPersonal,
  isCreating = false,
}: {
  chats: ProjectChat[];
  sharedChatId: string | null;
  currentUserId: string | undefined;
  activeChatId: string | null;
  onSelect: (chatId: string) => void;
  onNewPersonal: () => void;
  isCreating?: boolean;
}) {
  const isShared = (c: ProjectChat) =>
    c.projectChatKind === "shared" || c._id === sharedChatId;

  const sharedChats = chats.filter(isShared);
  const personalChats = chats.filter(
    (c) => !isShared(c) && (!currentUserId || c.userId === currentUserId),
  );

  return (
    <nav
      className="flex w-[248px] shrink-0 flex-col border-r border-puntt-neutral-gray-5 bg-puntt-neutral-gray-1"
      aria-label="Project chats"
      data-testid="campaign-thread-rail"
    >
      <div className="flex h-14 shrink-0 items-center gap-2 border-b border-puntt-neutral-gray-5 px-4">
        <Text
          size="3"
          weight="bold"
          className="flex-1 text-puntt-neutral-gray-12"
        >
          Ask Puntt
        </Text>
        <button
          type="button"
          onClick={onNewPersonal}
          disabled={isCreating}
          title="New personal chat"
          aria-label="New personal chat"
          data-testid="campaign-new-personal-chat"
          className="flex size-[30px] items-center justify-center rounded-md border border-puntt-neutral-gray-7 bg-base-white text-puntt-blue-11 transition-colors hover:border-puntt-accent-9 hover:bg-puntt-blue-2 hover:text-puntt-accent-9 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isCreating ? (
            <CircleNotch size={16} weight="bold" className="animate-spin" />
          ) : (
            <Plus size={16} weight="bold" />
          )}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-2.5 pb-4 pt-3">
        <ThreadSection label="Project" count={sharedChats.length} />
        {sharedChats.map((c) => (
          <ThreadItem
            key={c._id}
            chat={c}
            personal={false}
            active={c._id === activeChatId}
            onSelect={onSelect}
          />
        ))}

        <ThreadSection
          label="Personal"
          count={personalChats.length}
          badge="private"
        />
        {personalChats.length === 0 ? (
          <p className="px-2.5 pb-2 pt-1 text-xs leading-relaxed text-puntt-neutral-gray-9">
            No private chats. Start one to work with Puntt before sharing with
            the project.
          </p>
        ) : (
          personalChats.map((c) => (
            <ThreadItem
              key={c._id}
              chat={c}
              personal
              active={c._id === activeChatId}
              onSelect={onSelect}
            />
          ))
        )}
      </div>
    </nav>
  );
}

function ThreadSection({
  label,
  count,
  badge,
}: {
  label: string;
  count: number;
  badge?: string;
}) {
  return (
    <div className="mb-2 mt-4 flex items-center gap-2 px-2 first:mt-0.5">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-puntt-neutral-gray-11">
        {label}
      </span>
      <span className="font-mono text-[11px] text-puntt-neutral-gray-8">
        {count}
      </span>
      {badge && (
        <span className="rounded bg-puntt-neutral-gray-3 px-1.5 py-px text-[10px] font-medium text-puntt-neutral-gray-11">
          {badge}
        </span>
      )}
    </div>
  );
}

function ThreadItem({
  chat,
  personal,
  active,
  onSelect,
}: {
  chat: ProjectChat;
  personal: boolean;
  active: boolean;
  onSelect: (chatId: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(chat._id)}
      aria-current={active ? "true" : undefined}
      aria-label={`Open chat ${chat.title}`}
      data-testid={`campaign-thread-item-${chat._id}`}
      data-active={active ? "true" : "false"}
      className={`mb-0.5 flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-puntt-neutral-gray-3 ${
        active ? "bg-puntt-neutral-gray-4" : ""
      }`}
    >
      <span
        aria-hidden="true"
        className={`flex size-7 shrink-0 items-center justify-center rounded-md ${
          personal
            ? "border border-puntt-neutral-gray-7 bg-base-white text-puntt-blue-11"
            : "bg-puntt-blue-2 text-puntt-accent-9"
        }`}
      >
        {personal ? <Lock size={15} /> : <Users size={15} />}
      </span>
      <span className="min-w-0 flex-1">
        <span
          className={`block truncate text-[13px] font-semibold ${
            active ? "text-puntt-blue-11" : "text-puntt-neutral-gray-12"
          }`}
        >
          {chat.title}
        </span>
        <span className="mt-0.5 block truncate text-xs text-puntt-neutral-gray-11">
          {personal ? "Private draft" : "Project chat"}
        </span>
      </span>
    </button>
  );
}
